import { normalizeName } from '../funnel/normalize';
import { estimateCostUsd, parseModelRef } from './pricing';
import type {
  BotAction,
  BotConversationState,
  BotDecision,
  BotExtracted,
  BotIntent,
  BotStage,
  BotUnderstanding,
} from './types';

/**
 * Batería de escenarios (ADR-020 §8.5c): conversaciones escritas que se corren de corrido contra el bot con la ficha
 * de un proyecto real. Cada escenario dice qué debería entender y decidir el bot; sirve para probar el plan Básico,
 * medir un modelo y comparar varios (calidad, costo, tiempo). Los textos son genéricos: valen para cualquier proyecto.
 */
export const SCENARIO_GROUPS = [
  'CALIFICACION',
  'PREGUNTAS',
  'CITAS',
  'OBJECIONES',
  'CORTES',
  'RUIDO',
] as const;
export type ScenarioGroup = (typeof SCENARIO_GROUPS)[number];
export const SCENARIO_GROUP_LABELS: Record<ScenarioGroup, string> = {
  CALIFICACION: 'Calificación',
  PREGUNTAS: 'Preguntas',
  CITAS: 'Citas',
  OBJECIONES: 'Objeciones',
  CORTES: 'Cortes de conversación',
  RUIDO: 'Mensajes raros',
};

export interface ScenarioExpectation {
  /** Intención esperada por mensaje del cliente (mismo índice que `messages`); null = no se comprueba. */
  intents?: Array<BotIntent | BotIntent[] | null>;
  /** Acción esperada por mensaje del cliente. */
  actions?: Array<BotAction | BotAction[] | null>;
  finalStage?: BotStage;
  /** Datos que deben quedar en el estado al final. */
  data?: Partial<BotExtracted>;
  /** Ninguna respuesta del bot puede contener esto. */
  mustNotSay?: string[];
  /** Alguna respuesta del bot debe contener esto. */
  mustSay?: string[];
  /** En algún turno el bot pidió prioridad. */
  priority?: boolean;
  /** El celular o correo que escribió el cliente llegó tapado al modelo. */
  masked?: boolean;
  /** La cita agendada es virtual. */
  virtual?: boolean;
  /** Los horarios de la segunda propuesta son distintos de la primera. */
  newSlots?: boolean;
}

export interface BotScenario {
  id: string;
  title: string;
  group: ScenarioGroup;
  /** Mensajes del cliente en orden (el bot siempre escribe primero). */
  messages: string[];
  expect: ScenarioExpectation;
}

const many = (n: number, text: string) => Array.from({ length: n }, () => text);

export const BOT_SCENARIOS: BotScenario[] = [
  // ---------------------------------------------------------------- calificación
  {
    id: 'calificacion-paso-a-paso',
    title: 'Responde una pregunta por vez hasta la cita',
    group: 'CALIFICACION',
    messages: ['hola', 'para vivir', '2 dormitorios', '150 mil dólares', 'sí, el primero'],
    expect: {
      intents: ['SALUDO', 'INTERES', 'INTERES', 'INTERES', 'ACEPTA_CITA'],
      actions: ['RESPONDER', 'RESPONDER', 'RESPONDER', 'PROPONER_CITA', 'AGENDAR_CITA'],
      finalStage: 'CITA_AGENDADA',
      data: { purpose: 'VIVIENDA', bedrooms: 2, budget: 150000 },
    },
  },
  {
    id: 'todo-en-un-mensaje',
    title: 'Da todos los datos de una vez',
    group: 'CALIFICACION',
    messages: ['Busco para vivir, 2 dormitorios con 150 mil dólares'],
    expect: {
      intents: ['INTERES'],
      actions: ['PROPONER_CITA'],
      priority: true,
      data: { purpose: 'VIVIENDA', bedrooms: 2, budget: 150000, budgetCurrency: 'USD' },
    },
  },
  {
    id: 'estudio-para-invertir',
    title: 'Estudio para inversión',
    group: 'CALIFICACION',
    messages: ['quiero un estudio para invertir, tengo unos 90 mil'],
    expect: {
      actions: ['PROPONER_CITA'],
      data: { purpose: 'INVERSION', bedrooms: 0, budget: 90000 },
    },
  },
  {
    id: 'presupuesto-en-soles',
    title: 'Presupuesto en soles',
    group: 'CALIFICACION',
    messages: ['tengo S/ 350,000 para un depa de 3 dormitorios'],
    expect: {
      actions: ['PROPONER_CITA'],
      data: { bedrooms: 3, budget: 350000, budgetCurrency: 'PEN' },
    },
  },
  {
    id: 'solo-mirando',
    title: 'Solo está viendo, sin apuro',
    group: 'CALIFICACION',
    messages: ['solo estoy viendo, más adelante quizás'],
    expect: { actions: ['RESPONDER'], data: { horizon: 'MAS_12_MESES' }, priority: false },
  },
  {
    id: 'da-su-nombre',
    title: 'Se presenta con su nombre',
    group: 'CALIFICACION',
    messages: ['hola, me llamo Rosa y busco un depa'],
    expect: { intents: ['INTERES'], actions: ['RESPONDER'], data: { name: 'Rosa' } },
  },
  {
    id: 'financiamiento-tras-calificar',
    title: 'Pregunta por la inicial cuando ya empezó a calificar',
    group: 'CALIFICACION',
    messages: ['para vivir', '¿cuánto es la inicial?'],
    expect: {
      intents: ['INTERES', 'PREGUNTA_FINANCIAMIENTO'],
      actions: ['RESPONDER', 'PROPONER_CITA'],
      priority: true,
    },
  },
  // ---------------------------------------------------------------- preguntas
  {
    id: 'pregunta-precio',
    title: 'Pregunta el precio de entrada',
    group: 'PREGUNTAS',
    messages: ['¿cuánto cuesta el de 2 dormitorios?'],
    expect: {
      intents: ['PREGUNTA_PRECIO'],
      actions: ['RESPONDER'],
      mustNotSay: ['descuento', 'oferta'],
    },
  },
  {
    id: 'pregunta-financiamiento-primero',
    title: 'Pregunta por MiVivienda de entrada: es momento de ofrecer la cita',
    group: 'PREGUNTAS',
    messages: ['¿trabajan con MiVivienda?'],
    expect: {
      intents: ['PREGUNTA_FINANCIAMIENTO'],
      actions: ['PROPONER_CITA'],
      priority: true,
      mustNotSay: ['sin intereses'],
    },
  },
  {
    id: 'pregunta-ubicacion',
    title: 'Pregunta dónde queda',
    group: 'PREGUNTAS',
    messages: ['¿dónde queda el proyecto?'],
    expect: { intents: ['PREGUNTA_UBICACION'], actions: ['RESPONDER'] },
  },
  {
    id: 'pregunta-entrega',
    title: 'Pregunta la fecha de entrega',
    group: 'PREGUNTAS',
    messages: ['¿para cuándo es la entrega?'],
    expect: { intents: ['PREGUNTA_ENTREGA'], actions: ['RESPONDER'] },
  },
  {
    id: 'pregunta-frecuente',
    title: 'Pregunta algo de la lista de preguntas frecuentes',
    group: 'PREGUNTAS',
    messages: ['¿aceptan mascotas?'],
    expect: { intents: ['PREGUNTA_OTRA'], actions: ['RESPONDER'] },
  },
  {
    id: 'pregunta-sin-respuesta',
    title: 'Pregunta algo que no está en la ficha',
    group: 'PREGUNTAS',
    messages: ['¿el edificio tiene helipuerto?'],
    expect: {
      intents: ['PREGUNTA_OTRA'],
      actions: ['RESPONDER'],
      mustSay: ['asesor'],
    },
  },
  {
    id: 'precio-y-ubicacion',
    title: 'Dos preguntas en un mensaje',
    group: 'PREGUNTAS',
    messages: ['¿cuánto cuesta y dónde queda?'],
    expect: { intents: [['PREGUNTA_PRECIO', 'PREGUNTA_UBICACION']], actions: ['RESPONDER'] },
  },
  {
    id: 'area-y-estacionamiento',
    title: 'Pregunta por metraje y estacionamiento',
    group: 'PREGUNTAS',
    messages: ['¿de cuántos metros es y tiene estacionamiento?'],
    expect: { intents: ['PREGUNTA_OTRA'], actions: ['RESPONDER'] },
  },
  // ---------------------------------------------------------------- citas
  {
    id: 'pide-cita',
    title: 'Pide visitar la sala de ventas',
    group: 'CITAS',
    messages: ['quiero visitar la sala de ventas'],
    expect: { intents: ['PIDE_CITA'], actions: ['PROPONER_CITA'], priority: true },
  },
  {
    id: 'acepta-segundo-horario',
    title: 'Acepta el segundo horario',
    group: 'CITAS',
    messages: ['quiero una cita', 'el segundo'],
    expect: {
      intents: ['PIDE_CITA', 'ACEPTA_CITA'],
      actions: ['PROPONER_CITA', 'AGENDAR_CITA'],
      finalStage: 'CITA_AGENDADA',
    },
  },
  {
    id: 'propone-horario-propio',
    title: 'Propone su propio horario',
    group: 'CITAS',
    messages: ['quiero ir a ver el depa', 'puedo el sábado a las 11 am'],
    expect: {
      intents: ['PIDE_CITA', 'PROPONE_HORARIO'],
      actions: ['PROPONER_CITA', ['AGENDAR_CITA', 'PROPONER_CITA']],
    },
  },
  {
    id: 'propone-horario-fuera',
    title: 'Propone un horario fuera de la sala',
    group: 'CITAS',
    messages: ['quiero una cita', 'puedo mañana a las 11 pm'],
    expect: {
      intents: ['PIDE_CITA', 'PROPONE_HORARIO'],
      actions: ['PROPONER_CITA', 'PROPONER_CITA'],
    },
  },
  {
    id: 'rechaza-y-reacomoda',
    title: 'Rechaza los horarios y acepta los nuevos',
    group: 'CITAS',
    messages: ['quiero una cita', 'no puedo ninguno, otro día', 'sí, el primero'],
    expect: {
      intents: ['PIDE_CITA', 'RECHAZA_HORARIO', 'ACEPTA_CITA'],
      actions: ['PROPONER_CITA', 'PROPONER_CITA', 'AGENDAR_CITA'],
      newSlots: true,
    },
  },
  {
    id: 'cita-virtual',
    title: 'Pide una reunión virtual',
    group: 'CITAS',
    messages: ['quiero una reunión por zoom', 'sí, el primero'],
    expect: { actions: ['PROPONER_CITA', 'AGENDAR_CITA'], virtual: true },
  },
  {
    id: 'pregunta-durante-propuesta',
    title: 'Pregunta algo mientras hay horarios ofrecidos',
    group: 'CITAS',
    messages: ['quiero una cita', '¿hay estacionamiento?', 'ok, el primero'],
    expect: {
      actions: ['PROPONER_CITA', 'RESPONDER', 'AGENDAR_CITA'],
      finalStage: 'CITA_AGENDADA',
    },
  },
  {
    id: 'tres-propuestas-sin-cerrar',
    title: 'Rechaza tres veces: escala al asesor',
    group: 'CITAS',
    messages: ['quiero una cita', 'no puedo', 'tampoco puedo', 'no me acomoda ninguno'],
    expect: {
      actions: ['PROPONER_CITA', 'PROPONER_CITA', 'PROPONER_CITA', 'ESCALAR'],
      finalStage: 'ESCALADO',
    },
  },
  // ---------------------------------------------------------------- objeciones
  {
    id: 'muy-caro',
    title: 'Dice que está muy caro',
    group: 'OBJECIONES',
    messages: ['está muy caro para mí'],
    expect: { actions: ['RESPONDER'], mustNotSay: ['descuento', 'oferta', 'rebaja'] },
  },
  {
    id: 'no-interesa',
    title: 'Dice que no le interesa',
    group: 'OBJECIONES',
    messages: ['no me interesa, gracias'],
    expect: { intents: ['NO_INTERESA'], actions: ['CERRAR'], finalStage: 'CERRADO' },
  },
  {
    id: 'ya-compro',
    title: 'Ya compró en otro lado',
    group: 'OBJECIONES',
    messages: ['ya compré en otro proyecto'],
    expect: { intents: ['NO_INTERESA'], actions: ['CERRAR'] },
  },
  {
    id: 'numero-equivocado',
    title: 'Número equivocado',
    group: 'OBJECIONES',
    messages: ['número equivocado, no soy yo'],
    expect: { intents: ['NO_INTERESA'], actions: ['CERRAR'] },
  },
  // ---------------------------------------------------------------- cortes
  {
    id: 'pide-humano',
    title: 'Pide hablar con una persona',
    group: 'CORTES',
    messages: ['quiero hablar con una persona'],
    expect: {
      intents: ['PIDE_HUMANO'],
      actions: ['ESCALAR'],
      priority: true,
      finalStage: 'ESCALADO',
    },
  },
  {
    id: 'stop',
    title: 'Pide que no le escriban',
    group: 'CORTES',
    messages: ['no me escriban más'],
    expect: { intents: ['STOP'], actions: ['DETENER'], finalStage: 'DETENIDO' },
  },
  {
    id: 'stop-tras-cita',
    title: 'Corta después de pedir una cita',
    group: 'CORTES',
    messages: ['quiero una cita', 'basta, no me escriban'],
    expect: { actions: ['PROPONER_CITA', 'DETENER'], finalStage: 'DETENIDO' },
  },
  {
    id: 'celular-en-el-texto',
    title: 'Deja su celular y pide que lo llamen',
    group: 'CORTES',
    messages: ['mi cel es 987654321, llámenme por favor'],
    expect: { intents: ['PIDE_HUMANO'], actions: ['ESCALAR'], masked: true },
  },
  // ---------------------------------------------------------------- ruido
  {
    id: 'fuera-de-tema',
    title: 'Mensajes fuera de tema',
    group: 'RUIDO',
    messages: ['jajaja', 'y el clima cómo está'],
    expect: { intents: ['FUERA_DE_TEMA', null], actions: ['RESPONDER', 'RESPONDER'] },
  },
  {
    id: 'agradece',
    title: 'Solo agradece',
    group: 'RUIDO',
    messages: ['gracias'],
    expect: { intents: ['AGRADECE'], actions: ['RESPONDER'] },
  },
  {
    id: 'tope-de-turnos',
    title: 'Conversación que no avanza: tope de turnos',
    group: 'RUIDO',
    messages: many(13, '¿sigues ahí?'),
    expect: { finalStage: 'ESCALADO' },
  },
];

export function scenarioById(id: string): BotScenario | undefined {
  return BOT_SCENARIOS.find((s) => s.id === id);
}

/** Un turno tal como lo devuelve el motor (el primero es el mensaje inicial del bot, sin texto del cliente). */
export interface ScenarioTurn {
  customer: string | null;
  /** Texto del cliente tal como llegó al modelo (con celulares y correos tapados). */
  inputText: string | null;
  understanding: BotUnderstanding | null;
  decision: BotDecision;
  reply: string | null;
  verified: boolean;
  state: BotConversationState;
}

export interface ScenarioCheck {
  passed: number;
  total: number;
  failures: string[];
}

const norm = (s: string) => normalizeName(s);

/** Comprueba lo esperado contra los turnos obtenidos: cuántas comprobaciones pasaron y cuáles fallaron. */
export function checkScenario(scenario: BotScenario, turns: ScenarioTurn[]): ScenarioCheck {
  const failures: string[] = [];
  let total = 0;
  const check = (ok: boolean, why: string) => {
    total += 1;
    if (!ok) failures.push(why);
  };
  const e = scenario.expect;
  // turns[0] = mensaje inicial del bot; turns[i + 1] = respuesta al mensaje i del cliente.
  const answers = turns.slice(1);
  const last = turns[turns.length - 1];
  check(
    turns.length === scenario.messages.length + 1,
    `faltan turnos (${turns.length - 1} de ${scenario.messages.length})`,
  );
  (e.intents ?? []).forEach((want, i) => {
    if (!want) return;
    const got = answers[i]?.understanding?.intent ?? null;
    const list = Array.isArray(want) ? want : [want];
    check(
      !!got && list.includes(got),
      `mensaje ${i + 1}: entendió ${got ?? 'nada'}, se esperaba ${list.join(' o ')}`,
    );
  });
  (e.actions ?? []).forEach((want, i) => {
    if (!want) return;
    const got = answers[i]?.decision.action ?? null;
    const list = Array.isArray(want) ? want : [want];
    check(
      !!got && list.includes(got),
      `mensaje ${i + 1}: decidió ${got ?? 'nada'}, se esperaba ${list.join(' o ')}`,
    );
  });
  if (e.finalStage)
    check(
      last?.state.stage === e.finalStage,
      `terminó en ${last?.state.stage ?? '?'}, se esperaba ${e.finalStage}`,
    );
  if (e.data)
    for (const [k, v] of Object.entries(e.data)) {
      const got = (last?.state.data as Record<string, unknown> | undefined)?.[k];
      check(got === v, `dato ${k}: quedó ${String(got ?? 'vacío')}, se esperaba ${String(v)}`);
    }
  const replies = turns.map((t) => t.reply ?? '').filter(Boolean);
  for (const w of e.mustNotSay ?? [])
    check(!replies.some((r) => norm(r).includes(norm(w))), `el bot dijo "${w}"`);
  for (const w of e.mustSay ?? [])
    check(
      replies.some((r) => norm(r).includes(norm(w))),
      `el bot nunca dijo "${w}"`,
    );
  if (e.priority !== undefined)
    check(
      answers.some((t) => t.decision.priority) === e.priority,
      e.priority ? 'nunca pidió prioridad' : 'pidió prioridad sin motivo',
    );
  if (e.masked)
    check(
      answers.some((t) => (t.inputText ?? '').includes('[celular]')),
      'el celular no llegó tapado',
    );
  if (e.virtual)
    check(
      answers.some((t) => t.decision.appointment?.mode === 'VIRTUAL'),
      'la cita no quedó como virtual',
    );
  if (e.newSlots) {
    const proposals = answers.filter((t) => t.decision.action === 'PROPONER_CITA');
    const first = new Set(proposals[0]?.decision.slots.map((s) => s.at) ?? []);
    const second = proposals[1]?.decision.slots ?? [];
    check(
      second.length > 0 && second.every((s) => !first.has(s.at)),
      'la segunda propuesta repitió horarios',
    );
  }
  // Toda respuesta enviada pasó la verificación.
  check(
    turns.every((t) => !t.reply || t.verified),
    'alguna respuesta no pasó la verificación',
  );
  return { passed: total - failures.length, total, failures };
}

/**
 * Costo aproximado de correr la batería con un par de modelos: entrada ≈ ficha + instrucciones + historial,
 * salida ≈ JSON de entendimiento o una respuesta corta. Sirve para avisar antes de gastar; no es una factura.
 */
export function estimateBatteryCost(
  scenarios: BotScenario[],
  knowledgeChars: number,
  models: { understand: string | null; reply: string | null },
): { usd: number; calls: number } {
  const perCallInput = Math.ceil((knowledgeChars + 1800) / 4) + 200;
  let usd = 0;
  let calls = 0;
  for (const s of scenarios) {
    const understandCalls = s.messages.length;
    const replyCalls = s.messages.length + 1;
    if (models.understand) {
      usd +=
        understandCalls *
        estimateCostUsd(parseModelRef(models.understand).model, {
          input: perCallInput,
          cachedInput: 0,
          output: 120,
        });
      calls += understandCalls;
    }
    if (models.reply) {
      usd +=
        replyCalls *
        estimateCostUsd(parseModelRef(models.reply).model, {
          input: perCallInput,
          cachedInput: 0,
          output: 110,
        });
      calls += replyCalls;
    }
  }
  return { usd, calls };
}
